import React, { ReactElement, useCallback, useMemo, VFC } from 'react';
import classNames from 'classnames';
import { functionNoop } from 'src/utils/function/noop';

type Case = 'lowercase' | 'uppercase' | 'capitalize' | 'none';
type ItemOption = {
  case?: Case;
};
export type BreadcrumbContainer = {
  item: ReactElement;
  value: string;
};
export type Breadcrumb = string | BreadcrumbContainer;

interface BreadCrumbsComponentProps {
  items: Breadcrumb[];
  itemClassNames?: string[];
  itemOptions?: ItemOption[];
  onItemClick?: (option: string, index: number) => void;
  value?: string;
}

export const BreadCrumbsComponent: VFC<BreadCrumbsComponentProps> = ({
  items,
  itemClassNames = [],
  itemOptions = [],
  onItemClick = functionNoop,
  value,
}) => {
  const onItemClicked = useCallback(
    (option: string, index: number) => () => {
      onItemClick(option, index);
    },
    [onItemClick]
  );

  const valueIndex = useMemo(
    () =>
      items
        .reduce((acc: string[], cur) => {
          const curValue = typeof cur === 'string' ? cur : cur.value;
          return acc.concat(curValue);
        }, [])
        .indexOf(value || 'share'),
    [items, value]
  );

  return (
    <nav className='flex'>
      <ol className='flex items-center space-x-1'>
        {items
          .filter((_, index) => valueIndex >= index)
          .map((option, index, { length }) => {
            const isFirstOptionActive = valueIndex === 0;

            const itemOption = index < itemOptions.length ? itemOptions[index] : undefined;
            const value = typeof option === 'string' ? option : option.value;
            const item = typeof option === 'string' ? <span>{option}</span> : option.item;

            return (
              <li
                className={classNames(
                  'flex items-center has-hover text-bedrock-p-small-strong',
                  valueIndex !== index ? 'cursor-pointer' : 'pointer-events-none',
                  itemClassNames[index],
                  { lowercase: itemOption?.case === 'lowercase', capitalize: itemOption?.case === 'capitalize' },
                  `before:[&:not(:first-child)]:content-['/'] before:text-bedrock-dark-gray before:mr-1`,
                  isFirstOptionActive || valueIndex !== index ? 'text-bedrock-dark-gray' : 'text-bedrock-black'
                )}
                onClick={onItemClicked(value, index)}
                key={`BreadCrumbsComponent-${option}`}
              >
                {item}
              </li>
            );
          })}
      </ol>
    </nav>
  );
};
